<template>
  <section class="deduction-frequency-config" aria-labelledby="deduction-frequency-title">
    <div class="page-heading">
      <div>
        <h2 id="deduction-frequency-title">扣款频次</h2>
        <p>设置合利宝、快钱每日和每小时的最多收款单数。</p>
      </div>
      <a-button :loading="loading" :disabled="saving || dirty" @click="loadConfig">
        <template #icon><ReloadOutlined /></template>
        刷新配置
      </a-button>
    </div>

    <a-alert
      type="info"
      show-icon
      class="rule-description"
      message="每日 10:00–18:00（北京时间）按小时独立限额，其余时段禁止扣款。"
      description="按各渠道全部商户的成功收款记录累计，每日与每小时限额同时生效，达到任一限额后停止扣款；未使用的小时额度不顺延。小时额度填 0 表示该时段禁止扣款。"
    />

    <a-alert
      v-if="loadError"
      type="error"
      show-icon
      class="load-error"
      :message="loadError"
      description="请重新加载配置，加载成功后才能编辑和保存。"
    />

    <div class="channel-cards">
      <a-card v-for="channel in ['hlb', 'kq']" :key="channel" :bordered="false" class="config-card" :title="`${channel}（${channelNames[channel]}）`">
        <a-form layout="vertical" class="daily-limits">
          <a-form-item
            :label="`${channelNames[channel]}每日收款上限（单）`"
            :validate-status="loaded && dailyLimitError(dailyLimits[channel]) ? 'error' : ''"
            :help="loaded && dailyLimitError(dailyLimits[channel]) || '-1 不限制每日总数，0 禁止全天扣款，正整数限制每日总数。'"
          >
            <a-input-number
              v-model:value="dailyLimits[channel]"
              :min="-1"
              :max="MAX_LIMIT"
              :step="1"
              :disabled="!loaded || loading || saving"
              :aria-label="`${channelNames[channel]}每日收款上限`"
              placeholder="请输入每日上限"
              class="daily-input"
            />
          </a-form-item>
          <p class="daily-hint">每日上限设为 -1 时，仍遵循下方小时限额和扣款时段。</p>
        </a-form>
        <h2 class="hourly-heading">每小时收款上限</h2>
        <a-table
          :columns="columns"
          :data-source="hourlyLimits"
          :loading="loading"
          :pagination="false"
          :scroll="{ x: 340 }"
          row-key="hour"
          size="small"
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.dataIndex === 'hour'">
              <span class="hour-label">{{ formatHour(record.hour) }}</span>
            </template>
            <template v-else>
              <div class="limit-cell">
                <a-input-number
                  v-model:value="record[channel]"
                  :min="0"
                  :max="MAX_LIMIT"
                  :step="1"
                  :disabled="!loaded || loading || saving"
                  :status="limitError(record[channel]) ? 'error' : ''"
                  :aria-label="`${formatHour(record.hour)} ${channelNames[channel]}收款上限`"
                  :aria-invalid="!!limitError(record[channel])"
                  :aria-describedby="`limit-${record.hour}-${channel}`"
                  placeholder="请输入单数"
                  class="limit-input"
                />
                <span class="unit">单</span>
              </div>
              <div
                v-if="limitError(record[channel]) || record[channel] === 0"
                :id="`limit-${record.hour}-${channel}`"
                class="field-help"
                :class="{ 'field-error': limitError(record[channel]) }"
              >
                {{ limitError(record[channel]) || (record[channel] === 0 ? '该时段禁止扣款' : '') }}
              </div>
            </template>
          </template>
          <template #summary v-if="loaded">
            <a-table-summary>
              <a-table-summary-row>
                <a-table-summary-cell :index="0">小时额度合计</a-table-summary-cell>
                <a-table-summary-cell :index="1">{{ formatTotal(channel) }} 单</a-table-summary-cell>
              </a-table-summary-row>
            </a-table-summary>
          </template>
        </a-table>
      </a-card>
    </div>
    <div class="save-bar">
      <span class="save-hint" role="status">
        {{ dirty ? '有未保存的修改；保存后立即生效。' : '配置每天重复使用，保存后立即生效。' }}
      </span>
      <a-space>
        <a-button :disabled="!dirty || loading || saving" @click="resetChanges">撤销修改</a-button>
        <a-button type="primary" :loading="saving" :disabled="!canSave" @click="saveConfig">
          保存两渠道配置
        </a-button>
      </a-space>
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { message } from "ant-design-vue";
import { ReloadOutlined } from "@ant-design/icons-vue";
import { get, postJson } from "@/utils/request";
import { analyticsUrl } from "@/utils/api";

const CONFIG_API_PATH = "/operate/deduction-frequency/config";
const CONFIG_UPDATE_API_PATH = "/operate/deduction-frequency/config/update";
const MAX_LIMIT = 2147483647;
const HOURS = Array.from({ length: 8 }, (_, index) => index + 10);
const channelNames = { hlb: "合利宝", kq: "快钱" };
const columns = [
  { title: "时段（北京时间）", dataIndex: "hour", width: 160 },
  { title: "收款上限", dataIndex: "limit" },
];

const hourlyLimits = ref([]);
const savedLimits = ref([]);
const dailyLimits = ref({ hlb: null, kq: null });
const savedDailyLimits = ref({ hlb: null, kq: null });
const loading = ref(false);
const saving = ref(false);
const loaded = ref(false);
const loadError = ref("");

const formatHour = (hour) => `${hour}:00–${hour + 1}:00`;
const limitError = (value) => {
  if (value === null || value === undefined || value === "") return "请输入收款单数";
  if (typeof value !== "number" || !Number.isInteger(value) || value < 0 || value > MAX_LIMIT) {
    return `请输入 0–${MAX_LIMIT} 的整数`;
  }
  return "";
};
const dailyLimitError = (value) => {
  if (value === -1) return "";
  if (value === null || value === undefined || value === "") return "请输入每日收款上限";
  return limitError(value) ? `请输入 -1–${MAX_LIMIT} 的整数` : "";
};
const dirty = computed(() =>
  JSON.stringify(hourlyLimits.value) !== JSON.stringify(savedLimits.value) ||
  JSON.stringify(dailyLimits.value) !== JSON.stringify(savedDailyLimits.value)
);
const canSave = computed(() =>
  loaded.value && !loading.value && !saving.value && dirty.value &&
  !dailyLimitError(dailyLimits.value.hlb) && !dailyLimitError(dailyLimits.value.kq) &&
  hourlyLimits.value.length === HOURS.length &&
  hourlyLimits.value.every((row) => !limitError(row.hlb) && !limitError(row.kq))
);
const formatTotal = (channel) => {
  if (hourlyLimits.value.some((row) => limitError(row[channel]))) return "—";
  return hourlyLimits.value.reduce((total, row) => total + row[channel], 0).toLocaleString("zh-CN");
};

const applyConfig = (data) => {
  const rows = data?.hourlyLimits;
  if (!data?.dailyLimits || dailyLimitError(data.dailyLimits.hlb) || dailyLimitError(data.dailyLimits.kq)) {
    throw new Error("每日配置格式错误");
  }
  if (!Array.isArray(rows) || rows.length !== HOURS.length) throw new Error("小时配置不完整");
  const normalized = HOURS.map((hour) => {
    const matches = rows.filter((row) => row?.hour === hour);
    if (matches.length !== 1 || limitError(matches[0].hlb) || limitError(matches[0].kq)) {
      throw new Error("小时配置格式错误");
    }
    return { hour, hlb: matches[0].hlb, kq: matches[0].kq };
  });
  hourlyLimits.value = normalized;
  savedLimits.value = normalized.map((row) => ({ ...row }));
  dailyLimits.value = { hlb: data.dailyLimits.hlb, kq: data.dailyLimits.kq };
  savedDailyLimits.value = { ...dailyLimits.value };
  loaded.value = true;
};

const loadConfig = async () => {
  if (loading.value || saving.value || dirty.value) return;
  loading.value = true;
  loaded.value = false;
  loadError.value = "";
  try {
    const response = await get(analyticsUrl(CONFIG_API_PATH));
    if (response.code !== 200) throw new Error(response.content || "加载扣款频次失败");
    applyConfig(response.data);
  } catch (error) {
    loadError.value = error.message || error.content || "加载扣款频次失败";
  } finally {
    loading.value = false;
  }
};

const resetChanges = () => {
  if (loading.value || saving.value) return;
  hourlyLimits.value = savedLimits.value.map((row) => ({ ...row }));
  dailyLimits.value = { ...savedDailyLimits.value };
};

const saveConfig = async () => {
  if (!canSave.value) return;
  saving.value = true;
  try {
    const response = await postJson(analyticsUrl(CONFIG_UPDATE_API_PATH), {
      dailyLimits: { ...dailyLimits.value },
      hourlyLimits: hourlyLimits.value.map((row) => ({ ...row })),
    });
    if (response.code !== 200) throw new Error(response.content || "保存扣款频次失败");
    applyConfig(response.data);
    message.success("扣款频次已保存并生效");
  } catch (error) {
    message.error(error.message || error.content || "保存扣款频次失败");
  } finally {
    saving.value = false;
  }
};

onMounted(loadConfig);
</script>

<style scoped>
.deduction-frequency-config { margin-bottom: 24px; }
.page-heading { display: flex; justify-content: space-between; align-items: center; gap: 16px; margin-bottom: 20px; }
.page-heading h2 { margin: 0 0 6px; font-size: 20px; color: #172b4d; }
.page-heading p { margin: 0; color: #65758b; }
.rule-description, .load-error { margin-bottom: 16px; }
.config-card { border-radius: 12px; }
.channel-cards { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
.daily-limits { margin-bottom: 24px; border-bottom: 1px solid #edf0f5; padding-bottom: 16px; }
.daily-input { width: 100%; max-width: 280px; }
.daily-hint { color: #65758b; font-size: 13px; margin: 8px 0 0; }
.hourly-heading { font-size: 16px; margin: 0 0 16px; }
.hour-label { font-variant-numeric: tabular-nums; white-space: nowrap; }
.limit-cell { display: flex; align-items: center; gap: 8px; }
.limit-input { width: 140px; }
.unit, .field-help, .save-hint { color: #65758b; }
.field-help { min-height: 20px; margin-top: 3px; font-size: 12px; }
.field-error { color: #cf1322; }
.save-bar { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-top: 24px; }
.save-hint { font-size: 13px; }
.config-card :deep(.ant-table-summary) { font-weight: 600; background: #f5f8fc; }
@media (max-width: 768px) {
  .channel-cards { grid-template-columns: minmax(0, 1fr); }
  .page-heading { align-items: flex-start; }
  .save-bar { align-items: flex-start; flex-direction: column; }
  .save-bar :deep(.ant-space) { align-self: flex-end; }
  .deduction-frequency-config :deep(.ant-btn) { min-height: 44px; }
}
</style>
